import React, { useEffect, useState, useRef } from "react";
import { collection, query, where, onSnapshot, addDoc, updateDoc, doc, orderBy, serverTimestamp, getDocs, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { messaggioErrore } from "../utils/erroriUtente";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { useToast } from "../utils/Toast";
import { Send, Plus, X, Search, Users, Lock, Hash, ChevronDown, Circle, CheckCircle, Clock, Archive } from "lucide-react";
import { format } from "date-fns";
import { it } from "date-fns/locale";

// ─── Stato conversazione ───────────────────────────────────────────
const CONV_STATUS = [
  { id: "aperta", label: "Aperta", color: "#059669", icon: Circle },
  { id: "in_attesa", label: "In attesa", color: "#d97706", icon: Clock },
  { id: "chiusa", label: "Chiusa", color: "#6b7280", icon: Archive },
];

// ─── Nuovo modal conversazione ─────────────────────────────────────
function NewConversationModal({ orgId, currentUser, onClose, onCreated }) {
  const { addToast } = useToast();
  const [name, setName] = useState("");
  const [type, setType] = useState("gruppo"); // privata | gruppo
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [users, setUsers] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [clienteId, setClienteId] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getDocs(query(collection(db, "users"), where("organizationId", "==", orgId), where("enabled", "==", true)))
      .then(snap => setUsers(snap.docs.map(d => d.data()).filter(u => u.uid !== currentUser.uid)));
    getDocs(query(collection(db, "clienti"), where("organizationId", "==", orgId)))
      .then(snap => setClienti(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
  }, [orgId]);

  const toggleUser = uid => {
    setSelectedUsers(p => p.includes(uid) ? p.filter(x => x !== uid) : [...p, uid]);
  };

  const submit = async e => {
    e.preventDefault();
    if (type === "privata" && selectedUsers.length !== 1) { addToast("Seleziona esattamente 1 utente per chat privata", "error"); return; }
    if (type === "gruppo" && selectedUsers.length === 0) { addToast("Seleziona almeno un utente", "error"); return; }
    if (type === "gruppo" && !name.trim()) { addToast("Inserisci un nome per il gruppo", "error"); return; }
    setLoading(true);
    try {
      const members = [...selectedUsers, currentUser.uid];
      const convName = type === "privata"
        ? users.find(u => u.uid === selectedUsers[0])?.displayName || "Chat privata"
        : name.trim();
      const ref = await addDoc(collection(db, "conversations"), {
        name: convName,
        type,
        members,
        organizationId: orgId,
        clienteId: clienteId || null,
        status: "aperta",
        createdBy: currentUser.uid,
        createdAt: serverTimestamp(),
        lastMessage: null,
        lastMessageAt: serverTimestamp(),
        unreadCount: {},
      });
      addToast("Conversazione creata!", "success");
      onCreated({ id: ref.id, name: convName, type, members, status: "aperta" });
      onClose();
    } catch(err) { addToast(messaggioErrore(err), "error"); }
    setLoading(false);
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 440 }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20 }}>
          <h3 style={{ fontSize:16, fontWeight:700 }}>Nuova conversazione</h3>
          <button className="btn-icon" onClick={onClose}><X size={16}/></button>
        </div>

        <form onSubmit={submit}>
          {/* Tipo */}
          <div style={{ display:"flex", gap:8, marginBottom:16 }}>
            {[{ id:"privata", label:"💬 Privata", desc:"1 a 1" }, { id:"gruppo", label:"👥 Gruppo", desc:"Più persone" }].map(t => (
              <button key={t.id} type="button" onClick={() => setType(t.id)}
                style={{ flex:1, padding:"10px", border:`2px solid ${type===t.id?"#7c3aed":"#e5e7eb"}`, borderRadius:8, background:type===t.id?"#ede9fe":"transparent", cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                <div style={{ fontSize:13, fontWeight:600, color:type===t.id?"#7c3aed":"#374151" }}>{t.label}</div>
                <div style={{ fontSize:11, color:"#9ca3af" }}>{t.desc}</div>
              </button>
            ))}
          </div>

          {/* Nome gruppo */}
          {type === "gruppo" && (
            <div className="form-group">
              <label className="form-label">Nome gruppo</label>
              <input className="input" value={name} onChange={e => setName(e.target.value)} placeholder="Es. Team Manutenzione" required/>
            </div>
          )}

          {/* Selezione utenti */}
          <div className="form-group">
            <label className="form-label">{type === "privata" ? "Seleziona utente" : "Aggiungi membri"}</label>
            <div style={{ maxHeight:220, overflowY:"auto", border:"1px solid #e5e7eb", borderRadius:8 }}>
              {users.map(u => (
                <div key={u.uid} onClick={() => type === "privata" ? setSelectedUsers([u.uid]) : toggleUser(u.uid)}
                  style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 14px", cursor:"pointer", borderBottom:"1px solid #f3f4f6", background: selectedUsers.includes(u.uid) ? "#faf5ff" : "transparent" }}>
                  <div style={{ width:10, height:10, borderRadius:"50%", border:`2px solid ${selectedUsers.includes(u.uid)?"#7c3aed":"#e5e7eb"}`, background: selectedUsers.includes(u.uid)?"#7c3aed":"transparent", flexShrink:0 }}/>
                  <div className="avatar" style={{ width:28, height:28, fontSize:11 }}>{u.displayName?.[0]?.toUpperCase()}</div>
                  <div>
                    <div style={{ fontSize:13, fontWeight:500 }}>{u.displayName}</div>
                    <div style={{ fontSize:11, color:"#9ca3af" }}>{u.department || u.email}</div>
                  </div>
                </div>
              ))}
              {users.length === 0 && <div style={{ padding:16, textAlign:"center", fontSize:13, color:"#9ca3af" }}>Nessun altro utente</div>}
            </div>
          </div>

          {clienti.length > 0 && (
            <div className="form-group" style={{marginBottom:14}}>
              <label className="form-label">🏢 Collega a cliente (opzionale)</label>
              <select className="input" value={clienteId} onChange={e=>setClienteId(e.target.value)}>
                <option value="">Nessun cliente</option>
                {clienti.map(c => <option key={c.id} value={c.id}>{c.ragioneSociale||c.nome}</option>)}
              </select>
              <div style={{fontSize:11,color:"#9ca3af",marginTop:3}}>Apparirà in "Ultima comunicazione" nella scheda cliente</div>
            </div>
          )}

          <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Annulla</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>{loading?"Creo...":"Crea"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Singolo messaggio ─────────────────────────────────────────────
function MessageBubble({ msg, isOwn, showAvatar, authorName }) {
  const time = msg.createdAt ? format(msg.createdAt.toDate?.() ?? new Date(), "HH:mm") : "";
  return (
    <div style={{ display:"flex", flexDirection: isOwn ? "row-reverse" : "row", alignItems:"flex-end", gap:6, marginBottom:6 }}>
      {!isOwn && (
        <div className="avatar" style={{ width:26, height:26, fontSize:10, flexShrink:0, opacity: showAvatar ? 1 : 0 }}>
          {authorName?.[0]?.toUpperCase()}
        </div>
      )}
      <div style={{ maxWidth:"70%" }}>
        {!isOwn && showAvatar && <div style={{ fontSize:11, fontWeight:600, color:"#7c3aed", marginBottom:2, paddingLeft:4 }}>{authorName}</div>}
        <div style={{ background: isOwn ? "#7c3aed" : "#f3f4f6", color: isOwn ? "#fff" : "#111827", borderRadius: isOwn ? "16px 16px 4px 16px" : "16px 16px 16px 4px", padding:"8px 12px", fontSize:13, lineHeight:"1.5", wordBreak:"break-word" }}>
          {msg.text}
        </div>
        <div style={{ fontSize:10, color:"#9ca3af", marginTop:2, textAlign: isOwn ? "right" : "left", paddingLeft:4, paddingRight:4 }}>{time}</div>
      </div>
    </div>
  );
}

// ─── Pannello chat attiva ──────────────────────────────────────────
function ChatPanel({ conversation, currentUser, onStatusChange }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [showStatus, setShowStatus] = useState(false);
  const [members, setMembers] = useState([]);
  const bottomRef = useRef();
  const inputRef = useRef();

  useEffect(() => {
    if (!conversation) return;
    const q = query(collection(db, "messages"), where("conversationId", "==", conversation.id));
    return onSnapshot(q, snap => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => (a.createdAt?.toMillis?.() || 0) - (b.createdAt?.toMillis?.() || 0));
      setMessages(list);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior:"smooth" }), 100);
    });
  }, [conversation?.id]);

  useEffect(() => {
    if (!conversation?.members?.length) return;
    getDocs(query(collection(db, "users"), where("uid", "in", conversation.members)))
      .then(snap => setMembers(snap.docs.map(d => d.data())));
  }, [conversation?.members]);

  const send = async e => {
    e?.preventDefault();
    if (!text.trim() || loading || conversation?.status === "chiusa") return;
    setLoading(true);
    const msgText = text.trim();
    setText("");
    try {
      await addDoc(collection(db, "messages"), {
        conversationId: conversation.id,
        text: msgText,
        authorId: currentUser.uid,
        authorName: currentUser.displayName,
        createdAt: serverTimestamp(),
      });
      await updateDoc(doc(db, "conversations", conversation.id), {
        lastMessage: msgText,
        lastMessageAt: serverTimestamp(),
      });
    } catch(err) { setText(msgText); }
    setLoading(false);
    inputRef.current?.focus();
  };

  const changeStatus = async status => {
    await updateDoc(doc(db, "conversations", conversation.id), { status });
    setShowStatus(false);
    if (onStatusChange) onStatusChange(status);
  };

  const memberNames = members.filter(m => m.uid !== currentUser.uid).map(m => m.displayName).join(", ");
  const statusInfo = CONV_STATUS.find(s => s.id === (conversation?.status || "aperta"));
  const isClosed = conversation?.status === "chiusa";

  if (!conversation) return (
    <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", color:"#9ca3af", flexDirection:"column", gap:12 }}>
      <Hash size={40} style={{ opacity:0.3 }}/>
      <p style={{ fontSize:14 }}>Seleziona una conversazione</p>
    </div>
  );

  // Group messages by author for avatar display
  const enriched = messages.map((m, i) => ({
    ...m,
    isOwn: m.authorId === currentUser.uid,
    showAvatar: i === 0 || messages[i-1].authorId !== m.authorId,
  }));

  return (
    <div style={{ flex:1, display:"flex", flexDirection:"column", overflow:"hidden" }}>
      {/* Header */}
      <div style={{ padding:"12px 20px", borderBottom:"1px solid #e5e7eb", display:"flex", alignItems:"center", gap:12, flexShrink:0 }}>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:15, fontWeight:700, display:"flex", alignItems:"center", gap:8 }}>
            {conversation.type === "privata" ? <Lock size={14} color="#9ca3af"/> : <Users size={14} color="#9ca3af"/>}
            {conversation.name}
          </div>
          <div style={{ fontSize:12, color:"#9ca3af", marginTop:1 }}>
            {conversation.type === "privata" ? "Chat privata" : `${conversation.members?.length || 0} membri · ${memberNames}`}
          </div>
        </div>
        {/* Stato conversazione */}
        <div style={{ position:"relative" }}>
          <button onClick={() => setShowStatus(!showStatus)}
            style={{ display:"flex", alignItems:"center", gap:6, padding:"5px 12px", border:`1px solid ${statusInfo?.color}33`, borderRadius:20, background:`${statusInfo?.color}11`, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
            <div style={{ width:8, height:8, borderRadius:"50%", background: statusInfo?.color }}/>
            <span style={{ fontSize:12, fontWeight:600, color: statusInfo?.color }}>{statusInfo?.label}</span>
            <ChevronDown size={12} color={statusInfo?.color}/>
          </button>
          {showStatus && (
            <div style={{ position:"absolute", right:0, top:"100%", background:"#fff", border:"1px solid #e5e7eb", borderRadius:8, boxShadow:"0 8px 24px rgba(0,0,0,0.12)", zIndex:100, minWidth:140, padding:4, marginTop:4 }}>
              {CONV_STATUS.map(s => (
                <button key={s.id} onClick={() => changeStatus(s.id)}
                  style={{ width:"100%", padding:"8px 12px", border:"none", background: conversation.status===s.id?"#f9fafb":"transparent", cursor:"pointer", textAlign:"left", fontSize:13, display:"flex", alignItems:"center", gap:8, fontFamily:"'Inter',sans-serif", borderRadius:4, color: s.color, fontWeight: conversation.status===s.id?700:400 }}>
                  <div style={{ width:8, height:8, borderRadius:"50%", background: s.color, flexShrink:0 }}/>
                  {s.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Messaggi */}
      <div style={{ flex:1, overflowY:"auto", padding:"16px 20px" }}>
        {enriched.length === 0 ? (
          <div style={{ textAlign:"center", color:"#9ca3af", fontSize:13, marginTop:40 }}>
            <p>Nessun messaggio ancora.</p>
            <p style={{ marginTop:4 }}>Inizia la conversazione!</p>
          </div>
        ) : enriched.map(msg => (
          <MessageBubble key={msg.id} msg={msg} isOwn={msg.isOwn} showAvatar={msg.showAvatar} authorName={msg.authorName}/>
        ))}
        <div ref={bottomRef}/>
      </div>

      {/* Input */}
      {isClosed ? (
        <div style={{ padding:"14px 20px", borderTop:"1px solid #e5e7eb", background:"#f9fafb", textAlign:"center", fontSize:13, color:"#9ca3af" }}>
          Conversazione chiusa — riapri per inviare messaggi
        </div>
      ) : (
        <form onSubmit={send} style={{ padding:"12px 16px", borderTop:"1px solid #e5e7eb", display:"flex", gap:8, alignItems:"flex-end", flexShrink:0 }}>
          <textarea ref={inputRef} value={text} onChange={e => setText(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Scrivi un messaggio... (Invio per inviare, Shift+Invio per andare a capo)"
            rows={1}
            style={{ flex:1, resize:"none", border:"1px solid #e5e7eb", borderRadius:10, padding:"9px 14px", fontSize:13, fontFamily:"'Inter',sans-serif", outline:"none", lineHeight:"1.5", minHeight:40, maxHeight:120, overflowY:"auto" }}
            onInput={e => { e.target.style.height = "auto"; e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px"; }}/>
          <button type="submit" disabled={!text.trim() || loading}
            style={{ width:40, height:40, borderRadius:"50%", background: text.trim() ? "#7c3aed" : "#e5e7eb", border:"none", cursor: text.trim() ? "pointer" : "not-allowed", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, transition:"background 0.15s" }}>
            <Send size={16} color={text.trim() ? "#fff" : "#9ca3af"}/>
          </button>
        </form>
      )}
    </div>
  );
}

// ─── Pagina principale Chat ────────────────────────────────────────
export default function ChatPage() {
  const { currentUser, userProfile } = useAuth();
  const { effectiveOrgId: orgId } = useOrg();
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("tutte");

  useEffect(() => {
    if (!orgId || !currentUser) return;
    const q = query(
      collection(db, "conversations"),
      where("organizationId", "==", orgId),
      where("members", "array-contains", currentUser.uid)
    );
    return onSnapshot(q, snap => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => (b.lastMessageAt?.toMillis?.() || 0) - (a.lastMessageAt?.toMillis?.() || 0));
      setConversations(list);
    });
  }, [orgId, currentUser]);

  const filtered = conversations.filter(c => {
    if (filterStatus !== "tutte" && c.status !== filterStatus) return false;
    if (search && !c.name?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  // Segna conversazione come letta da questo utente
  const markAsRead = async (conv) => {
    try {
      await updateDoc(doc(db, "conversations", conv.id), {
        [`lastReadBy.${currentUser.uid}`]: serverTimestamp()
      });
    } catch(e) {}
  };

  // Conteggio conversazioni non lette (per badge / situazione operativa)
  const unreadCount = conversations.filter(c => {
    if (!c.lastMessageAt) return false;
    const lastRead = c.lastReadBy?.[currentUser.uid];
    if (!lastRead) return true;
    return c.lastMessageAt.toMillis?.() > lastRead.toMillis?.();
  }).length;

  const statusInfo = id => CONV_STATUS.find(s => s.id === id) || CONV_STATUS[0];
  const timeLabel = conv => {
    if (!conv.lastMessageAt) return "";
    try {
      const d = conv.lastMessageAt.toDate?.();
      if (!d) return "";
      const now = new Date();
      const diff = now - d;
      if (diff < 60000) return "ora";
      if (diff < 3600000) return `${Math.floor(diff/60000)}m`;
      if (diff < 86400000) return format(d, "HH:mm");
      return format(d, "d MMM", { locale: it });
    } catch { return ""; }
  };

  return (
    <div style={{ display:"flex", height:"100%", overflow:"hidden" }}>
      {/* Lista conversazioni */}
      <div style={{ width:300, borderRight:"1px solid #e5e7eb", display:"flex", flexDirection:"column", flexShrink:0 }}>
        {/* Header */}
        <div style={{ padding:"14px 16px", borderBottom:"1px solid #e5e7eb", flexShrink:0 }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
            <h3 style={{ fontSize:15, fontWeight:700 }}>Chat</h3>
            <button onClick={() => setShowNew(true)} className="btn btn-primary btn-sm">
              <Plus size={13}/> Nuova
            </button>
          </div>
          {/* Ricerca */}
          <div style={{ position:"relative", marginBottom:10 }}>
            <Search size={13} style={{ position:"absolute", left:9, top:"50%", transform:"translateY(-50%)", color:"#9ca3af" }}/>
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Cerca conversazione..."
              style={{ width:"100%", paddingLeft:28, paddingRight:10, paddingTop:7, paddingBottom:7, border:"1px solid #e5e7eb", borderRadius:8, fontSize:12, fontFamily:"'Inter',sans-serif", outline:"none", boxSizing:"border-box" }}/>
          </div>
          {/* Filtro stato */}
          <div style={{ display:"flex", gap:4 }}>
            {[{ id:"tutte", label:"Tutte" }, ...CONV_STATUS].map(s => (
              <button key={s.id} onClick={() => setFilterStatus(s.id)}
                style={{ padding:"3px 8px", borderRadius:20, border:"1px solid", borderColor: filterStatus===s.id?"#7c3aed":"#e5e7eb", background: filterStatus===s.id?"#ede9fe":"transparent", color: filterStatus===s.id?"#7c3aed":"#6b7280", fontSize:11, fontWeight:500, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Lista */}
        <div style={{ flex:1, overflowY:"auto" }}>
          {filtered.length === 0 ? (
            <div style={{ padding:24, textAlign:"center", color:"#9ca3af", fontSize:13 }}>
              {search ? "Nessun risultato" : "Nessuna conversazione ancora"}
            </div>
          ) : filtered.map(conv => {
            const si = statusInfo(conv.status || "aperta");
            const isSelected = selected?.id === conv.id;
            const isUnread = conv.lastMessageAt && (!conv.lastReadBy?.[currentUser.uid] || conv.lastMessageAt.toMillis?.() > conv.lastReadBy[currentUser.uid].toMillis?.()) && conv.lastMessage;
            return (
              <div key={conv.id} onClick={() => { setSelected(conv); markAsRead(conv); }}
                style={{ padding:"12px 16px", borderBottom:"1px solid #f3f4f6", cursor:"pointer", background: isSelected ? "#faf5ff" : "transparent", transition:"background 0.1s" }}
                onMouseOver={e => !isSelected && (e.currentTarget.style.background = "#f9fafb")}
                onMouseOut={e => !isSelected && (e.currentTarget.style.background = "transparent")}>
                <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                  <div style={{ width:38, height:38, borderRadius:"50%", background: conv.type==="privata" ? "#e0e7ff" : "#ede9fe", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, fontSize:16 }}>
                    {conv.type === "privata" ? "💬" : "👥"}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                      <span style={{ fontSize:13, fontWeight: isUnread ? 800 : 600, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", color: isSelected?"#7c3aed":"#111827" }}>
                        {isUnread && <span style={{display:"inline-block",width:7,height:7,borderRadius:"50%",background:"#2563eb",marginRight:6}}/>}
                        {conv.name}
                      </span>
                      <span style={{ fontSize:10, color:"#9ca3af", flexShrink:0, marginLeft:6 }}>{timeLabel(conv)}</span>
                    </div>
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:2 }}>
                      <span style={{ fontSize:11, color: isUnread ? "#374151" : "#9ca3af", fontWeight: isUnread ? 600 : 400, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                        {conv.lastMessage || "Nessun messaggio"}
                      </span>
                      <div style={{ width:7, height:7, borderRadius:"50%", background: si.color, flexShrink:0, marginLeft:6 }} title={si.label}/>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Pannello chat */}
      <ChatPanel
        conversation={selected}
        currentUser={currentUser}
        onStatusChange={status => setSelected(c => c ? {...c, status} : c)}
      />

      {showNew && (
        <NewConversationModal
          orgId={orgId}
          currentUser={currentUser}
          onClose={() => setShowNew(false)}
          onCreated={conv => setSelected(conv)}
        />
      )}
    </div>
  );
}
